import React, { useState, useEffect, useRef } from "react";
import { userService } from "../../_services";
import { generateMemberId } from "../../utils/cardHelpers";
import { Navigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Printer, Maximize2, X, Loader2 } from "lucide-react";
import CardFront from "../Summary/CardFront";
import CardBack from "../Summary/CardBack";
import FlyerPage1 from "./flyer1";
import FlyerPage2 from "./flyer2";
import pdf1 from '../../resources/pricing-sheets/NuLifeSpan.Top.50.Walgreens.Mar25.pdf';
import pdf2 from '../../resources/pricing-sheets/NuLifeSpan.Top.CVS.Mar.25.pdf';
import pdf3 from '../../resources/pricing-sheets/NuLifeSpan.Walmart.top.Mar.25.pdf';

/**
 * Order Cards Page
 * Modernized with shadcn components - Order cards and download flyers/pricing sheets
 */
function OrderCardsPage() {
    // State
    const [groupNumbers, setGroupNumbers]   = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [groupStatus, setGroupStatus]     = useState('');
    const [groupData, setGroupData]         = useState(null);
    const [isPrimary, setIsPrimary]         = useState('');
    const [cardsData, setCardsData]         = useState([]);
    const [zoomLeft, setZoomLeft]           = useState(false);
    const [zoomRight, setZoomRight]         = useState(false);
    const [pricingSheetData, setPricingSheetData] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPrinting, setIsPrinting]       = useState(false);
    const [cardsLoading, setCardsLoading]   = useState(true);

    // Refs for flyer composeCanvas access (useRef so they persist across renders)
    const flyer1Ref = useRef(null);
    const flyer2Ref = useRef(null);

    // Refs for PDF iframes
    const iframeRefs = {
        pdf1: useRef(null),
        pdf2: useRef(null),
        pdf3: useRef(null),
    };

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        getUserGroups(userId, userName);
        getPricingSheets(userId);

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const getUserGroups = async (userId, userName) => {
        try {
            const apidata = await userService.GetUserGroups(userId, userName);
            if (apidata && apidata.length > 0) {
                setGroupNumbers(apidata);
                setSelectedGroup(apidata[0].groupnumber);
                setGroupStatus(apidata[0].status);
                setIsPrimary(apidata[0].isPrimary);
                setGroupData(apidata[0]);
                setCardsData(getCardsDataForGroup(apidata[0].status));
            }
        } catch (error) {
            console.error('Error fetching user groups:', error);
        } finally {
            setCardsLoading(false);
        }
    };

    const getPricingSheets = async (userId) => {
        try {
            const apiData = await userService.FetchPricingSheets(userId);
            if (apiData && apiData.length > 0) setPricingSheetData(apiData);
        } catch (error) {
            console.error('Error fetching pricing sheets:', error);
        }
    };

    const getCardsDataForGroup = (status) => {
        let cards = [{
            cardname: 'Glic Card',
            classnames: 'glic_card',
            bin: '023518',
            pcn: 'ARX',
        }];

        if (status === 'Yes') {
            cards.push(
                { cardname: 'Citizen Card', classnames: 'citizen_card', bin: '015284', pcn: 'CRX'  },
                { cardname: 'NVT Card',     classnames: 'nvt_card',     bin: '610602', pcn: 'NVT'  },
                { cardname: 'CVS Card',     classnames: 'cvs_card',     bin: '610398', pcn: 'CV1'  },
                { cardname: 'DST Card',     classnames: 'dst_card',     bin: '600428', pcn: 'HRX'  }
            );
        }

        return cards.map(card => ({ ...card, mId: generateMemberId(card.classnames) }));
    };

    const handleGroupChange = (value) => {
        setSelectedGroup(value);
        const selected = groupNumbers.find(g => g.groupnumber === value);
        if (selected) {
            setGroupData(selected);
            setIsPrimary(selected.isPrimary);
            setGroupStatus(selected.status);
            setCardsData(getCardsDataForGroup(selected.status));
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') { setZoomLeft(false); setZoomRight(false); }
    };

    // --- Helper: apply rounded corners to a data-URL -------------------------
    const applyRoundedCorners = (dataUrl, radius = 16) => {
        return new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
                const c   = document.createElement('canvas');
                c.width   = img.width;
                c.height  = img.height;
                const ctx = c.getContext('2d');
                const r   = radius * (img.width / 480); // scale radius to actual size
                ctx.beginPath();
                ctx.moveTo(r, 0);
                ctx.lineTo(c.width - r, 0);
                ctx.quadraticCurveTo(c.width, 0, c.width, r);
                ctx.lineTo(c.width, c.height - r);
                ctx.quadraticCurveTo(c.width, c.height, c.width - r, c.height);
                ctx.lineTo(r, c.height);
                ctx.quadraticCurveTo(0, c.height, 0, c.height - r);
                ctx.lineTo(0, r);
                ctx.quadraticCurveTo(0, 0, r, 0);
                ctx.closePath();
                ctx.clip();
                ctx.drawImage(img, 0, 0);
                resolve(c.toDataURL('image/png'));
            };
            img.src = dataUrl;
        });
    };

    // --- Helper: img element ? data URL --------------------------------------
    const imgToDataUrl = (imgEl) => {
        return new Promise(resolve => {
            const c   = document.createElement('canvas');
            c.width   = imgEl.naturalWidth  || imgEl.width;
            c.height  = imgEl.naturalHeight || imgEl.height;
            const ctx = c.getContext('2d');
            ctx.drawImage(imgEl, 0, 0);
            resolve(c.toDataURL('image/png'));
        });
    };

    // --- Download individual card ---------------------------------------------
    const downloadIndividual = async (cardIndex, cardName) => {
        try {
            const cardEl     = document.querySelectorAll('.cards')[cardIndex];
            if (!cardEl) return;
            const frontCanvas = cardEl.querySelector('canvas');
            const backImg     = cardEl.querySelector('img[alt="Prescription Savings Card \u2014 Back"]');

            const CARD_W = 160; // mm
            const pdf    = new jsPDF('p', 'mm', 'a4');
            const pdfW   = pdf.internal.pageSize.getWidth();
            const pdfH   = pdf.internal.pageSize.getHeight();

            if (frontCanvas) {
                const frontDataUrl  = frontCanvas.toDataURL('image/png');
                const roundedFront  = await applyRoundedCorners(frontDataUrl);
                const frontAspect   = frontCanvas.height / frontCanvas.width;
                const frontH        = CARD_W * frontAspect;
                const frontX        = (pdfW - CARD_W) / 2;
                const frontY        = (pdfH - frontH * 2 - 6) / 2;
                pdf.addImage(roundedFront, 'PNG', frontX, frontY, CARD_W, frontH);

                if (backImg) {
                    const backDataUrl  = await imgToDataUrl(backImg);
                    const roundedBack  = await applyRoundedCorners(backDataUrl);
                    const backAspect   = backImg.naturalHeight / backImg.naturalWidth;
                    const backH        = CARD_W * backAspect;
                    pdf.addImage(roundedBack, 'PNG', frontX, frontY + frontH + 6, CARD_W, backH);
                }
            }

            pdf.save(cardName + '.pdf');
        } catch (error) {
            console.error('Error downloading card:', error);
        }
    };

    // --- Print individual card ------------------------------------------------
    const printIndividual = async (cardIndex) => {
        const cardEl = document.querySelectorAll('.cards')[cardIndex];
        if (!cardEl) return;

        const frontCanvas = cardEl.querySelector('canvas');
        const backImg     = cardEl.querySelector('img[alt="Prescription Savings Card \u2014 Back"]');

        let frontSrc = '';
        let backSrc  = '';

        if (frontCanvas) frontSrc = frontCanvas.toDataURL('image/png');
        if (backImg)     backSrc  = backImg.src;

        const html = `
            <div style="text-align:center;padding:20px;max-width:700px;margin:0 auto;">
                ${frontSrc ? `<img src="${frontSrc}" style="width:100%;border-radius:16px;display:block;margin-bottom:16px;" />` : ''}
                ${backSrc  ? `<img src="${backSrc}"  style="width:100%;border-radius:16px;display:block;" />` : ''}
            </div>`;
        document.body.innerHTML = html;
        setTimeout(() => { window.print(); window.location.reload(); }, 500);
    };

    // --- Download all (cards + flyers) ---------------------------------------
    const downloadAll = async () => {
        setIsDownloading(true);
        try {
            // Cards
            const cardEls = document.querySelectorAll('.cards');
            for (let i = 0; i < cardEls.length; i++) {
                const cardEl      = cardEls[i];
                const frontCanvas = cardEl.querySelector('canvas');
                const backImg     = cardEl.querySelector('img[alt="Prescription Savings Card \u2014 Back"]');
                const CARD_W = 160;
                const pdf    = new jsPDF('p', 'mm', 'a4');
                const pdfW   = pdf.internal.pageSize.getWidth();
                const pdfH   = pdf.internal.pageSize.getHeight();

                if (frontCanvas) {
                    const frontDataUrl = frontCanvas.toDataURL('image/png');
                    const roundedFront = await applyRoundedCorners(frontDataUrl);
                    const frontAspect  = frontCanvas.height / frontCanvas.width;
                    const frontH       = CARD_W * frontAspect;
                    const frontX       = (pdfW - CARD_W) / 2;
                    const frontY       = (pdfH - frontH * 2 - 6) / 2;
                    pdf.addImage(roundedFront, 'PNG', frontX, frontY, CARD_W, frontH);

                    if (backImg) {
                        const backDataUrl  = await imgToDataUrl(backImg);
                        const roundedBack  = await applyRoundedCorners(backDataUrl);
                        const backAspect   = backImg.naturalHeight / backImg.naturalWidth;
                        const backH        = CARD_W * backAspect;
                        pdf.addImage(roundedBack, 'PNG', frontX, frontY + frontH + 6, CARD_W, backH);
                    }
                }
                pdf.save(`card-${i + 1}.pdf`);
            }

            // Flyer 1
            if (flyer1Ref.current) {
                const composite = await flyer1Ref.current.composeCanvas();
                if (composite) {
                    const pdf  = new jsPDF('p', 'mm', 'a4');
                    const pdfW = pdf.internal.pageSize.getWidth();
                    const pdfH = pdf.internal.pageSize.getHeight();
                    const ratio = Math.min((pdfW - 10) / (composite.width / 2), (pdfH - 10) / (composite.height / 2));
                    const w = (composite.width  / 2) * ratio;
                    const h = (composite.height / 2) * ratio;
                    pdf.addImage(composite.toDataURL('image/png'), 'PNG', (pdfW - w) / 2, (pdfH - h) / 2, w, h);
                    pdf.save('flyer-1.pdf');
                }
            }

            // Flyer 2
            if (flyer2Ref.current) {
                const composite = await flyer2Ref.current.composeCanvas();
                if (composite) {
                    const pdf  = new jsPDF('p', 'mm', 'a4');
                    const pdfW = pdf.internal.pageSize.getWidth();
                    const pdfH = pdf.internal.pageSize.getHeight();
                    const ratio = Math.min((pdfW - 10) / (composite.width / 2), (pdfH - 10) / (composite.height / 2));
                    const w = (composite.width  / 2) * ratio;
                    const h = (composite.height / 2) * ratio;
                    pdf.addImage(composite.toDataURL('image/png'), 'PNG', (pdfW - w) / 2 + 5, 10, w, h);
                    pdf.save('flyer-2.pdf');
                }
            }

            // Pricing sheets
            [pdf1, pdf2, pdf3].forEach((pdfUrl, index) => {
                setTimeout(() => handleDownload(pdfUrl), index * 500);
            });
        } catch (error) {
            console.error('Error in downloadAll:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    // --- Print all (cards + flyers) ------------------------------------------
    const printAll = async () => {
        setIsPrinting(true);
        try {
            const cardEls = document.querySelectorAll('.cards');
            let printParts = [];

            // Cards
            for (let i = 0; i < cardEls.length; i++) {
                const cardEl      = cardEls[i];
                const frontCanvas = cardEl.querySelector('canvas');
                const backImg     = cardEl.querySelector('img[alt="Prescription Savings Card \u2014 Back"]');
                let frontSrc = '', backSrc = '';
                if (frontCanvas) frontSrc = frontCanvas.toDataURL('image/png');
                if (backImg)     backSrc  = backImg.src;
                printParts.push(`
                    <div style="text-align:center;max-width:700px;margin:0 auto 40px;">
                        ${frontSrc ? `<img src="${frontSrc}" style="width:100%;border-radius:16px;display:block;margin-bottom:16px;" />` : ''}
                        ${backSrc  ? `<img src="${backSrc}"  style="width:100%;border-radius:16px;display:block;" />` : ''}
                    </div>`);
            }

            // Flyer 1
            if (flyer1Ref.current) {
                const composite = await flyer1Ref.current.composeCanvas();
                if (composite) {
                    printParts.push(`
                        <div style="text-align:center;max-width:700px;margin:0 auto 40px;">
                            <img src="${composite.toDataURL('image/png')}" style="width:100%;height:auto;display:block;" />
                        </div>`);
                }
            }

            // Flyer 2
            if (flyer2Ref.current) {
                const composite = await flyer2Ref.current.composeCanvas();
                if (composite) {
                    printParts.push(`
                        <div style="text-align:center;max-width:700px;margin:0 auto 40px;">
                            <img src="${composite.toDataURL('image/png')}" style="width:100%;height:auto;display:block;" />
                        </div>`);
                }
            }

            document.body.innerHTML = `<div style="padding:20px;">${printParts.join('')}</div>`;
            setTimeout(() => { window.print(); window.location.reload(); }, 500);
        } catch (error) {
            console.error('Error in printAll:', error);
            window.location.reload();
        }
    };

    const handleDownload = (pdfUrl) => {
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = pdfUrl.split("/").pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrint = (pdfKey) => {
        if (iframeRefs[pdfKey]?.current) {
            iframeRefs[pdfKey].current.contentWindow.focus();
            iframeRefs[pdfKey].current.contentWindow.print();
        }
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/signup" replace />;
    }

    localStorage.setItem('pagename', 'ordercards');

    return (
        <div className="w-full space-y-6">
            {/* Heading row — title/subtitle left, action buttons right (stacked on mobile) */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-2">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight text-foreground">
                        Marketing Materials
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        The assets below are your personal cards and flyers to print and share.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button onClick={downloadAll} disabled={cardsLoading || isDownloading} variant="secondary">
                        <Download className="mr-2 h-4 w-4" />
                        {isDownloading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Downloading</> : 'Download All'}
                    </Button>
                    <Button onClick={printAll} variant="outline" disabled={cardsLoading || isPrinting}>
                        <Printer className="mr-2 h-4 w-4" />
                        Print All
                    </Button>
                </div>
            </div>

            {/* Group selector — right-aligned, stacked label above select */}
            {groupNumbers.length > 0 && (
                <div className="flex flex-col items-end gap-1 mb-2">
                    <label className="text-sm font-medium text-primary w-44 text-left">
                        Select Group Number:
                    </label>
                    <Select value={selectedGroup} onValueChange={handleGroupChange}>
                        <SelectTrigger className="w-44">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {groupNumbers.map((group, index) => (
                                <SelectItem key={index} value={group.groupnumber}>
                                    {group.groupnumber}
                                    {group.isPrimary === 'True' && ' (Primary)'}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            {/* Cards Section */}
            <div className="all_cards space-y-4">
                {cardsData.map((card, index) => (
                    <Card key={index} className="cards">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-lg font-semibold text-primary">
                                {card.cardname}
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="ghost" onClick={() => downloadIndividual(index, card.cardname)}>
                                    <Download className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => printIndividual(index)}>
                                    <Printer className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <CardFront
                                    cardData={{ bin: card.bin, pcn: card.pcn, classnames: card.classnames, mId: card.mId }}
                                    PrimaryGn={selectedGroup}
                                    mId={card.mId}
                                    beneficiaryName={groupData?.beneficiaryName || ''}
                                />
                                <CardBack
                                    cardData={{ bin: card.bin, pcn: card.pcn, classnames: card.classnames, mId: card.mId }}
                                />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Flyers Section */}
            <div className="space-y-4">
                <h2 className="text-xl font-semibold text-primary">Printable Flyers</h2>
                <div className="grid grid-cols-1 gap-4 flyers">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-lg font-semibold text-primary">Flyer 1</CardTitle>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="ghost" onClick={() => setZoomLeft(true)}>
                                    <Maximize2 className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={async () => { if (flyer1Ref.current) await flyer1Ref.current.downloaddoc(); }}>
                                    <Download className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={async () => { if (flyer1Ref.current) await flyer1Ref.current.printIndivisual(); }}>
                                    <Printer className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[175px] sm:h-[350px] overflow-hidden cursor-pointer" onClick={() => setZoomLeft(true)}>
                                <FlyerPage1
                                    ref={flyer1Ref}
                                    bin={cardsData[0]?.bin}
                                    pcn={cardsData[0]?.pcn}
                                    classnames={cardsData[0]?.classnames}
                                    groupNumber={selectedGroup}
                                    mId={cardsData[0]?.mId}
                                    groupData={groupData}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-lg font-semibold text-primary">Flyer 2</CardTitle>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="ghost" onClick={() => setZoomRight(true)}>
                                    <Maximize2 className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={async () => { if (flyer2Ref.current) await flyer2Ref.current.downloaddoc(); }}>
                                    <Download className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={async () => { if (flyer2Ref.current) await flyer2Ref.current.printIndivisual(); }}>
                                    <Printer className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[175px] sm:h-[350px] overflow-hidden cursor-pointer" onClick={() => setZoomRight(true)}>
                                <FlyerPage2
                                    ref={flyer2Ref}
                                    bin={cardsData[0]?.bin}
                                    pcn={cardsData[0]?.pcn}
                                    classnames={cardsData[0]?.classnames}
                                    groupNumber={selectedGroup}
                                    mId={cardsData[0]?.mId}
                                    groupData={groupData}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Pricing Sheets Section */}
            <div className="pricing-sheets space-y-4">
                <h2 className="text-xl font-semibold text-primary">Pricing Sheets</h2>
                <div className="grid grid-cols-1 gap-4">
                    {[
                        { pdf: pdf1, key: 'pdf1', title: 'Walgreens Pricing' },
                        { pdf: pdf2, key: 'pdf2', title: 'CVS Pricing' },
                        { pdf: pdf3, key: 'pdf3', title: 'Walmart Pricing' }
                    ].map((item) => (
                        <Card key={item.key}>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="text-base font-semibold text-primary">
                                    {item.title}
                                </CardTitle>
                                <div className="flex items-center gap-2">
                                    <Button size="sm" variant="ghost" onClick={() => handleDownload(item.pdf)}>
                                        <Download className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => handlePrint(item.key)}>
                                        <Printer className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <iframe
                                    ref={iframeRefs[item.key]}
                                    src={`${item.pdf}#toolbar=0&navpanes=0&scrollbar=0`}
                                    className="w-full h-[175px] sm:h-[350px] border rounded"
                                    title={item.title}
                                />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Zoomed Flyer 1 */}
            {zoomLeft && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
                    <div className="bg-card rounded-lg w-[700px] max-w-full max-h-[90vh] overflow-auto relative">
                        <Button
                            size="sm" variant="ghost"
                            onClick={() => setZoomLeft(false)}
                            className="absolute top-2 right-2 z-10 bg-card hover:bg-gray-100 rounded-full"
                        >
                            <X className="h-5 w-5" />
                        </Button>
                        <FlyerPage1
                            bin={cardsData[0]?.bin}
                            pcn={cardsData[0]?.pcn}
                            classnames={cardsData[0]?.classnames}
                            groupNumber={selectedGroup}
                            mId={cardsData[0]?.mId}
                            groupData={groupData}
                        />
                    </div>
                </div>
            )}

            {/* Zoomed Flyer 2 */}
            {zoomRight && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
                    <div className="bg-card rounded-lg w-[700px] max-w-full max-h-[90vh] overflow-auto relative">
                        <Button
                            size="sm" variant="ghost"
                            onClick={() => setZoomRight(false)}
                            className="absolute top-2 right-2 z-10 bg-card hover:bg-gray-100 rounded-full"
                        >
                            <X className="h-5 w-5" />
                        </Button>
                        <FlyerPage2
                            bin={cardsData[0]?.bin}
                            pcn={cardsData[0]?.pcn}
                            classnames={cardsData[0]?.classnames}
                            groupNumber={selectedGroup}
                            mId={cardsData[0]?.mId}
                            groupData={groupData}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default OrderCardsPage;

